import React, { FC, memo, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { DepositHistory, useGetDepositHistoryQuery } from 'entities/Deposit';
import { Modal } from 'shared/ui/Modal';
import { Table } from 'shared/ui/Table';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';

interface DepositHistoryModalProps {
  isOpened: boolean;
  depositId: string;
  onClose: () => void;
}

const getDiffValueClassName = (diffValue: number): string => {
  return `text-sm font-semibold ${diffValue > 0 ? 'text-green-600' : 'text-red-600'}`;
};

const renderAmountWithDiff = (amount: number, diffAmount: number, currencySymbol: string): JSX.Element => (
  <div className="text-primary font-normal">
    <div>
      {roundNumber(amount)} {currencySymbol}
    </div>
    {diffAmount !== 0 && (
      <div className={getDiffValueClassName(diffAmount)}>
        {diffAmount > 0 ? '+' : ''}
        {roundNumber(diffAmount)} {currencySymbol}
      </div>
    )}
  </div>
);

export const DepositHistoryModal: FC<DepositHistoryModalProps> = memo((props) => {
  const { isOpened, depositId, onClose } = props;

  const { t } = useAppTranslation('contracts');
  const currencySymbol = useGetCurrencySymbol();

  const { data: depositHistory, isLoading } = useGetDepositHistoryQuery({ depositId }, { skip: !isOpened || !depositId });

  const columns = useMemo<ColumnsType<DepositHistory>>(
    () => [
      {
        title: t('Date'),
        key: 'createdAt',
        width: '15%',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
      {
        title: t('Description'),
        key: 'description',
        width: '25%',
        render: (_, record) => {
          const splitedDescription = splitDocumentDescription(record.description);

          return (
            <div className="text-primary min-h-[30px] font-normal">
              {t(splitedDescription.description)} {splitedDescription.documentNumber}
            </div>
          );
        },
      },
      {
        title: t('Deposit amount'),
        key: 'depositAmount',
        width: '15%',
        render: (_, record) => renderAmountWithDiff(record.depositAmount, record.depositAmountDiff, currencySymbol),
      },
      {
        title: t('Charged amount'),
        key: 'chargedDepositAmount',
        width: '15%',
        render: (_, record) => renderAmountWithDiff(record.chargedDepositAmount, record.chargedDepositAmountDiff, currencySymbol),
      },
      {
        title: t('Cancelled amount'),
        key: 'cancelledDepositAmount',
        width: '15%',
        render: (_, record) => renderAmountWithDiff(record.cancelledDepositAmount, record.cancelledDepositAmountDiff, currencySymbol),
      },
      {
        title: t('Refunded amount'),
        key: 'refundedDepositAmount',
        width: '15%',
        render: (_, record) => renderAmountWithDiff(record.refundedDepositAmount, record.refundedDepositAmountDiff, currencySymbol),
      },
    ],
    [t, currencySymbol],
  );

  return (
    <Modal width={1200} isOpen={isOpened} onClose={onClose}>
      <div className="mb-10">
        <Table
          title={<div className="font-semibold text-2xl">{t('Deposit History')}</div>}
          columns={columns}
          data={depositHistory || []}
          loading={isLoading}
          rowKey="depositHistoryId"
          search={false}
          pagination={{
            pageSize: 10,
          }}
        />
      </div>
    </Modal>
  );
});
